import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { isValidAddressComponent, isValidAddressInput, isValidEmail, isValidName, valueIsNotNullOrUndefined } from './validation'
import { isValidPostcode } from './postcode-validations'
import { CheckoutAddress } from '../model'
import { CountryCode } from 'libphonenumber-js/types'
import { InputAddressData } from '@afterpaytouch/core'
import { BLACKLISTED_STATES } from '../model/paymentType'

export interface AddressValidationConfig {
  state: boolean
  postcode: boolean
  suburb: boolean
}

export const requiredAddressFieldsByCountry: Record<CountryCode | string, AddressValidationConfig> = {
  AU: {
    state: true,
    postcode: true,
    suburb: true,
  },
  NZ: {
    state: true,
    postcode: true,
    suburb: true,
  },
  US: {
    state: true,
    postcode: true,
    suburb: true,
  },
  CA: {
    state: true,
    postcode: true,
    suburb: true,
  },
  GB: {
    state: false,
    postcode: true,
    suburb: true,
  },
  IM: {
    state: false,
    postcode: true,
    suburb: true,
  },
  JE: {
    state: false,
    postcode: true,
    suburb: true,
  },
  GG: {
    state: false,
    postcode: true,
    suburb: true,
  },
  DEFAULT: {
    state: false,
    postcode: false,
    suburb: true,
  },
}

export const validateProfileField = (country, key, value, isMobileLogin = false): boolean => {
  const requiredFields = requiredAddressFieldsByCountry[country] ?? requiredAddressFieldsByCountry.DEFAULT

  const validate: Record<string, (input: string) => boolean> = {
    givenNames: (name) => isValidName({ name }),
    surname: (name) => isValidName({ name }),
    otherGivenNames: (name) => isValidName({ name, optional: true }),
    dateOfBirth: (input) => !isUndefinedOrEmpty(input),
    mobile: (input) => !isUndefinedOrEmpty(input),
    address1: (input) => isValidAddressComponent({ input }),
    address2: (input) => isValidAddressComponent({ input, optional: true }),
    area2: (input) => isValidAddressComponent({ input, optional: true }),
    countryCode: (input) => !isUndefinedOrEmpty(input),
    postcode: (input) => isValidPostcode(country, input, !requiredFields.postcode),
    state: (input) => isValidAddressComponent({ input, optional: !requiredFields.state }),
    suburb: (input) => isValidAddressComponent({ input, optional: !requiredFields.suburb }),
    email: (input) => (isMobileLogin ? isValidEmail(input) : true),
    creditCheckAgree: (input) => valueIsNotNullOrUndefined(input),
  }

  return validate[key](value)
}

export const isValidAddress = (address: CheckoutAddress | undefined): boolean => {
  const requiredFields = requiredAddressFieldsByCountry[address?.countryCode ?? 'DEFAULT'] ?? requiredAddressFieldsByCountry.DEFAULT

  return (
    isValidAddressInput(address?.address1) &&
    isValidPostcode(address?.countryCode, address?.postcode, !requiredFields.postcode) &&
    isValidAddressInput(address?.state, !requiredFields.state) &&
    isValidAddressInput(address?.suburb, !requiredFields.suburb)
  )
}

export const getAddressOptionalFields = (country): string[] => {
  const requiredFields = requiredAddressFieldsByCountry[country] ?? requiredAddressFieldsByCountry.DEFAULT
  return Object.keys(requiredFields).filter((key) => requiredFields[key] === false)
}

export const isConsumerLendingEligibleState = (address: InputAddressData): boolean => {
  const ineligibleStates: string[] = BLACKLISTED_STATES.PCL[address?.countryCode]
  return !ineligibleStates?.includes(address?.state)
}

interface IsStateEligibleForPayByInstallmentArgs {
  countryCode: string
  state?: string
}

export const isStateEligibleForPayByInstallment = (address: IsStateEligibleForPayByInstallmentArgs): boolean => {
  const ineligibleStates: string[] = BLACKLISTED_STATES.PBI[address?.countryCode]
  return !ineligibleStates?.includes(address?.state ?? '')
}
